import React, { useState, useEffect, useRef } from 'react';
import { apiService, regionManager } from '../services/api';
import { PlatformRegion } from '../types';
import WPMLogo from './WPMLogo';

interface HeaderProps {
  username?: string;
}

const ChevronDown: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    className={`w-4 h-4 shrink-0 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GlobeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4 shrink-0 text-gray-500">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3c-2.5 3-4 5.7-4 9s1.5 6 4 9" />
    <path d="M12 3c2.5 3 4 5.7 4 9s-1.5 6-4 9" />
    <path d="M3 12h18" />
  </svg>
);

const Header: React.FC<HeaderProps> = ({ username }) => {
  const [regions, setRegions] = useState<PlatformRegion[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchRegions = async () => {
      setIsLoadingRegions(true);
      try {
        const regionsData = await apiService.getMyRegions();
        setRegions(regionsData);

        const currentRegion = regionManager.getRegion();
        if (currentRegion) {
          setSelectedRegion(currentRegion);
        } else if (regionsData.length > 0) {
          const firstRegion = regionsData[0].name;
          regionManager.setRegion(firstRegion);
          setSelectedRegion(firstRegion);
        }
      } catch {
        // ignore
      } finally {
        setIsLoadingRegions(false);
      }
    };

    fetchRegions();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleRegionSelect = (regionName: string) => {
    if (regionName === selectedRegion) {
      setDropdownOpen(false);
      return;
    }
    regionManager.setRegion(regionName);
    setSelectedRegion(regionName);
    setDropdownOpen(false);
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch {
      // ignore
    }
    window.location.href = '/login';
  };

  const selectedLabel = regions.find((r) => r.name === selectedRegion);
  const displayLabel = selectedLabel
    ? `${selectedLabel.name} — ${selectedLabel.environment}`
    : selectedRegion || (isLoadingRegions ? 'Loading…' : 'No regions');

  return (
    <header className="bg-white px-6 shadow-sm border-b border-gray-200">
      <div className="flex justify-between items-center h-[60px]">
        {/* Left: logo (visible when sidebar is collapsed or on narrow screens) */}
        <div className="flex items-center gap-2.5">
          <WPMLogo size={28} />
          <span className="text-lg font-bold text-slate-800 tracking-wide">WPM</span>
        </div>

        {/* Right: region selector + user + logout */}
        <div className="flex items-center gap-4">
          {/* Custom region dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => !isLoadingRegions && setDropdownOpen((o) => !o)}
              disabled={isLoadingRegions}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-purple-500 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-w-[160px]"
            >
              <GlobeIcon />
              <span className="flex-1 text-left truncate">{displayLabel}</span>
              <ChevronDown open={dropdownOpen} />
            </button>

            {dropdownOpen && regions.length > 0 && (
              <div className="absolute right-0 mt-1.5 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Select Region</p>
                </div>
                <ul className="py-1 max-h-64 overflow-y-auto">
                  {regions.map((region) => {
                    const isSelected = region.name === selectedRegion;
                    return (
                      <li key={region.id}>
                        <button
                          onClick={() => handleRegionSelect(region.name)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                            isSelected
                              ? 'bg-purple-50 text-purple-700'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="flex-1 font-medium">{region.name}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              region.environment === 'production'
                                ? 'bg-red-100 text-red-600'
                                : region.environment === 'staging'
                                ? 'bg-amber-100 text-amber-600'
                                : 'bg-blue-100 text-blue-600'
                            }`}
                          >
                            {region.environment}
                          </span>
                          {isSelected && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4 text-purple-600 shrink-0">
                              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Username */}
          <span className="text-sm text-gray-600 font-medium hidden sm:block">{username || 'User'}</span>

          {/* Logout */}
          <button
            className="bg-red-500 text-white border-0 px-4 py-1.5 rounded-lg cursor-pointer text-sm font-medium transition-colors hover:bg-red-600"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
